import { useMemo } from 'react';
import useSWR from 'swr';

import type { MapWorkbenchRuntimeSurfaceSectionRenderArgs } from '@workspace/ui';

import type { SalesWorkspaceContext } from '@/lib/sales-collaboration';
import {
  loadSalesLocationSummaries,
  type SalesLocationSummaryItem,
  type SalesLocationSummaryRequestLocation,
} from '@/lib/sales-location-summaries';

const readString = (value: unknown) => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
};

export const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '—';
  }
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) {
    return value;
  }
  return new Date(parsed).toLocaleString();
};

export const buildSalesLocationSummaryRequestLocation = (
  feature: MapWorkbenchRuntimeSurfaceSectionRenderArgs['feature'],
): SalesLocationSummaryRequestLocation | null => {
  const properties = feature.properties ?? {};
  const featureIdentityKey =
    readString(properties.feature_identity_key) ??
    readString(feature.id);
  if (!featureIdentityKey) {
    return null;
  }
  return {
    feature_identity_key: featureIdentityKey,
    location_id:
      readString(properties.location_id) ??
      readString(properties.locationId) ??
      readString(properties.id),
    display_name: readString(properties.display_name) ?? undefined,
    address_line1: readString(properties.address_line1) ?? undefined,
    locality: readString(properties.locality) ?? undefined,
    region_code: readString(properties.region_code) ?? undefined,
    postal_code: readString(properties.postal_code) ?? undefined,
    country_code: readString(properties.country_code) ?? undefined,
  };
};

export const buildSalesLocationSummaryKey = (
  requestLocation: SalesLocationSummaryRequestLocation | null,
  salesContext?: SalesWorkspaceContext | null,
) =>
  requestLocation
    ? [
        'sales-location-summary',
        requestLocation.feature_identity_key,
        requestLocation.location_id ?? '',
        salesContext?.catalog_owner_tenant_id ?? '',
        salesContext?.entitlement_id ?? '',
      ]
    : null;

export function useSalesLocationSummary(
  feature: MapWorkbenchRuntimeSurfaceSectionRenderArgs['feature'],
  salesContext?: SalesWorkspaceContext | null,
) {
  const requestLocation = useMemo(
    () => buildSalesLocationSummaryRequestLocation(feature),
    [feature],
  );

  const swrKey = buildSalesLocationSummaryKey(requestLocation, salesContext);

  const { data, error, isLoading, mutate } = useSWR<SalesLocationSummaryItem | null>(
    swrKey,
    async () => {
      if (!requestLocation) {
        return null;
      }
      const response = await loadSalesLocationSummaries([requestLocation], salesContext);
      return response.items[0] ?? null;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    },
  );

  return {
    requestLocation,
    summary: data ?? null,
    error,
    isLoading,
    refresh: mutate,
  };
}
